import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Gamepad2, Gift, Loader2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { gameLinkService, type GameLinkCode, type GameLinkStatus } from '@/lib/services/game-link.service';

/**
 * Vincular a conta da loja ao jogador no servidor (/vincular), no perfil.
 *
 * Location: src/components/conta
 * Why: o código nasce aqui, com o jogador logado, e é digitado no jogo pelo
 * jogador com o mesmo nick: é isso que prova que os dois são a mesma pessoa.
 * Vinculado, ele resgata presentes no jogo com /resgatar.
 */
export function GameLinkCard({ storeId }: { storeId: string }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<GameLinkStatus | null>(null);
  const [codigo, setCodigo] = useState<GameLinkCode | null>(null);
  const [gerando, setGerando] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => {
    gameLinkService
      .status(storeId)
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [storeId]);

  // Enquanto há código na tela: conta o tempo e confere se o jogo já vinculou.
  useEffect(() => {
    if (!codigo) return;
    const relogio = window.setInterval(() => setAgora(Date.now()), 1000);
    const conferir = window.setInterval(() => {
      gameLinkService
        .status(storeId)
        .then((s) => {
          if (s.linked && s.linkedAt && (!status?.linkedAt || s.linkedAt !== status.linkedAt)) {
            setStatus(s);
            setCodigo(null);
            toast.success(t('store.game_link.linked_now'));
          }
        })
        .catch(() => undefined);
    }, 4000);
    return () => {
      window.clearInterval(relogio);
      window.clearInterval(conferir);
    };
  }, [codigo, storeId, status?.linkedAt, t]);

  async function gerar() {
    setGerando(true);
    try {
      setCodigo(await gameLinkService.gerarCodigo(storeId));
      setAgora(Date.now());
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('store.game_link.error'));
    } finally {
      setGerando(false);
    }
  }

  async function desvincular() {
    setDesvinculando(true);
    try {
      setStatus(await gameLinkService.desvincular(storeId));
      toast.success(t('store.game_link.unlinked'));
    } catch {
      toast.error(t('store.game_link.error'));
    } finally {
      setDesvinculando(false);
    }
  }

  const restante = codigo ? Math.max(0, Math.floor((new Date(codigo.expiresAt).getTime() - agora) / 1000)) : 0;
  // O nome dos comandos vem da loja (o lojista pode ter trocado /vincular no painel).
  const vincular = codigo?.command ?? status?.command ?? '/vincular';
  const resgatar = codigo?.redeemCommand ?? status?.redeemCommand ?? '/resgatar';
  const comando = codigo ? `${vincular} ${codigo.code}` : '';
  const venceu = !!codigo && restante === 0;

  return (
    <Card className="border-border/40" data-testid="game-link-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gamepad2 className="h-5 w-5 text-primary" />
          {t('store.game_link.title')}
        </CardTitle>
        <CardDescription>{t('store.game_link.description', { redeem: resgatar })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status?.linked ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="game-link-linked">
            <p className="text-sm">
              {t('store.game_link.linked', {
                nick: status.nick,
                date: status.linkedAt ? new Date(status.linkedAt).toLocaleDateString(i18n.language) : '',
              })}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={desvinculando}>
                  <Unlink className="mr-2 h-4 w-4" />
                  {t('store.game_link.unlink')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('store.game_link.unlink_confirm')}</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('store.game_link.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => void desvincular()}>{t('store.game_link.unlink')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('store.game_link.how', { nick: status?.nick ?? '' })}</p>
        )}

        {codigo && !venceu ? (
          <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-4 text-center" data-testid="game-link-code">
            <p className="text-sm text-muted-foreground">{t('store.game_link.type_in_game')}</p>
            <div className="flex items-center justify-center gap-2">
              <code className="rounded-md bg-background px-3 py-2 font-mono text-xl font-bold tracking-widest">{comando}</code>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('store.game_link.copy')}
                onClick={() => {
                  void navigator.clipboard?.writeText(comando);
                  toast.success(t('store.game_link.copied'));
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('store.game_link.expires', { min: Math.floor(restante / 60), s: String(restante % 60).padStart(2, '0') })}
            </p>
          </div>
        ) : (
          <Button onClick={() => void gerar()} disabled={gerando} data-testid="game-link-generate">
            {gerando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gamepad2 className="mr-2 h-4 w-4" />}
            {t(venceu ? 'store.game_link.generate_again' : status?.linked ? 'store.game_link.generate_relink' : 'store.game_link.generate')}
          </Button>
        )}

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Gift className="mt-0.5 h-4 w-4 shrink-0" />
          {t('store.game_link.gift_hint', { resgatar })}
        </p>
      </CardContent>
    </Card>
  );
}
